import { useCallback, useRef } from 'react'
import type { DocumentRecord } from '@/types/catalog'
import type { CardPositions, Connection, PendingConnection, CardPosition } from './types'
import PageCard from './PageCard'
import ConnectionLayer from './ConnectionLayer'

interface PageMappingCanvasProps {
  documents: DocumentRecord[]
  cardPositions: CardPositions
  onCardMove: (docId: string, pos: CardPosition) => void
  connections: Connection[]
  onConnectionAdd: (sourceId: string, targetId: string) => void
  selectedCardId: string | null
  onCardSelect: (docId: string | null) => void
  onCardPreview: (doc: DocumentRecord) => void
  pendingConnection: PendingConnection | null
  onPendingConnectionChange: (pc: PendingConnection | null) => void
}

const CANVAS_MIN_WIDTH = 1400
const CANVAS_MIN_HEIGHT = 900

export default function PageMappingCanvas({
  documents,
  cardPositions,
  onCardMove,
  connections,
  onConnectionAdd,
  selectedCardId,
  onCardSelect,
  onCardPreview,
  pendingConnection,
  onPendingConnectionChange,
}: PageMappingCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null)

  // Compute canvas size from card positions
  const positions = Object.values(cardPositions)
  const canvasWidth = Math.max(CANVAS_MIN_WIDTH, ...positions.map((p) => p.x + 400))
  const canvasHeight = Math.max(CANVAS_MIN_HEIGHT, ...positions.map((p) => p.y + 200))

  const handleCanvasClick = useCallback(() => {
    onCardSelect(null)
    onPendingConnectionChange(null)
  }, [onCardSelect, onPendingConnectionChange])

  const handleCanvasMouseMove = useCallback((e: React.MouseEvent) => {
    if (!pendingConnection || !canvasRef.current) return
    const rect = canvasRef.current.getBoundingClientRect()
    onPendingConnectionChange({
      ...pendingConnection,
      mousePos: { x: e.clientX - rect.left + canvasRef.current.scrollLeft, y: e.clientY - rect.top + canvasRef.current.scrollTop },
    })
  }, [pendingConnection, onPendingConnectionChange])

  const handlePortClick = useCallback((docId: string, side: 'left' | 'right') => {
    if (!pendingConnection && side === 'right') {
      // Start a new connection from the right port
      const pos = cardPositions[docId]
      if (pos) {
        onPendingConnectionChange({ sourceId: docId, mousePos: { x: pos.x + 320, y: pos.y + 30 } })
      }
    } else if (pendingConnection && side === 'left' && docId !== pendingConnection.sourceId) {
      // Complete the connection to the left port
      onConnectionAdd(pendingConnection.sourceId, docId)
      onPendingConnectionChange(null)
    }
  }, [pendingConnection, cardPositions, onConnectionAdd, onPendingConnectionChange])

  return (
    <div
      ref={canvasRef}
      className="flex-1 min-h-0 overflow-auto relative page-mapping-canvas"
      onClick={handleCanvasClick}
      onMouseMove={handleCanvasMouseMove}
    >
      <div className="relative" style={{ width: canvasWidth, height: canvasHeight }}>
        <ConnectionLayer
          connections={connections}
          cardPositions={cardPositions}
          pendingConnection={pendingConnection}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
        />

        {documents.map((doc) => {
          const pos = cardPositions[doc.id]
          if (!pos) return null
          return (
            <PageCard
              key={doc.id}
              docId={doc.id}
              docName={doc.name}
              position={pos}
              isSelected={selectedCardId === doc.id}
              onSelect={() => onCardSelect(doc.id)}
              onMove={(newPos) => onCardMove(doc.id, newPos)}
              onPreview={() => onCardPreview(doc)}
              onPortClick={handlePortClick}
              showPorts={selectedCardId === doc.id || pendingConnection !== null}
            />
          )
        })}
      </div>
    </div>
  )
}
